// PlanogramClipboard
//
// Models the two clipboard interactions that existed as near-duplicate
// useState/useRef soup in BOTH PlanogramVisual.tsx and PlanogramTabular.tsx:
//
//   1. Mouse-driven "copy mode": click a cell's copy icon (copySourceCell),
//      then click/drag over target cells to stamp the source onto them
//      (copiedCellKeys accumulates as you go). Each cell's pre-overwrite
//      data is also captured into restoreSnapshots as it's stamped, so
//      cancelling the session can hand every affected cell back exactly
//      what it held before the session started (see GH #515 — Cancel used
//      to only clear copySource/copiedKeys, never actually undoing the
//      onDuplicateCell writes that had already happened).
//
//   2. Keyboard clipboard (Ctrl+C / Ctrl+X / Ctrl+V): copy/cut a cell or a
//      whole selection, then paste relative to whichever cell is active —
//      this only existed in PlanogramVisual previously.
//
// This class only holds the STATE/SHAPE of those two concepts and the pure
// math for "what would pasting at this anchor produce" — it does not own
// React state itself (that's `usePlanogramClipboard`) and it does NOT call
// onDuplicateCell/onClearCell/onRestoreCell (those are side effects the
// hook performs after asking this class what to do / what was captured).
// ---------------------------------------------------------------------------

import { CellCoordinates, CellKey, cellKey, parseCellKey } from './types';

export interface ClipboardSnapshot {
    cells: CellCoordinates[];
    anchor: CellCoordinates;
    isCut: boolean;
}

export class PlanogramClipboard {
    private constructor(
        /** Mouse copy-mode: the single cell currently being copied from, or null if copy mode is inactive. */
        public readonly copySource: CellCoordinates | null,
        /** Mouse copy-mode: every target cell that's been stamped with the source so far (for the checkmark/counter UI). */
        public readonly copiedKeys: ReadonlySet<CellKey>,
        /**
         * Mouse copy-mode: for every target cell stamped so far, whatever
         * was there immediately BEFORE it got overwritten with the copy
         * source — keyed by target cell, one entry per distinct cell (a
         * cell stamped twice in the same session keeps its ORIGINAL
         * pre-session value, not the intermediate one, so Cancel always
         * restores all the way back to where the session started). See
         * GH #515: without this, Cancel only ever reset the copy-mode UI
         * state (copySource/copiedKeys) — the actual onDuplicateCell
         * writes that already happened were never undone, so cancelling
         * left every copied-into cell holding the copied data permanently.
         */
        public readonly restoreSnapshots: ReadonlyMap<CellKey, unknown>,
        /** Keyboard clipboard: last Ctrl+C/X payload, or null. */
        public readonly keyboardClipboard: ClipboardSnapshot | null,
    ) {}

    static empty(): PlanogramClipboard {
        return new PlanogramClipboard(null, new Set(), new Map(), null);
    }

    // -----------------------------------------------------------------------
    // Mouse copy-mode
    // -----------------------------------------------------------------------

    startCopyMode(rowIndex: number, colIndex: number): PlanogramClipboard {
        return new PlanogramClipboard({ rowIndex, colIndex }, new Set(), new Map(), this.keyboardClipboard);
    }

    /**
     * Ends copy mode WITHOUT restoring anything — the caller has either
     * already replayed `restoreSnapshots` (Cancel) or deliberately wants
     * to keep the copied values (Save/confirm). Callers needing the
     * revert behavior should read `restoreSnapshots` BEFORE calling this,
     * not rely on this method to do it — this class only holds state/pure
     * math, it never performs the onRestoreCell side effect itself (same
     * separation of concerns as markCopied not calling onDuplicateCell).
     */
    cancelCopyMode(): PlanogramClipboard {
        return new PlanogramClipboard(null, new Set(), new Map(), this.keyboardClipboard);
    }

    get isCopyModeActive(): boolean {
        return this.copySource !== null;
    }

    isCopySourceCell(rowIndex: number, colIndex: number): boolean {
        return this.copySource?.rowIndex === rowIndex && this.copySource?.colIndex === colIndex;
    }

    /**
     * Records that `targetRowIndex/targetColIndex` has now been stamped
     * with the copy source (UI bookkeeping only — caller still has to
     * perform the actual duplicateCell side effect). `previousCellData` is
     * whatever was at that target cell immediately before this call — the
     * caller reads it (e.g. from its own planogramData) right before
     * invoking onDuplicateCell, and passes it in here so it can be handed
     * back on cancel.
     */
    markCopied(targetRowIndex: number, targetColIndex: number, previousCellData?: unknown): PlanogramClipboard {
        if (!this.copySource) return this;
        if (this.copySource.rowIndex === targetRowIndex && this.copySource.colIndex === targetColIndex) return this;

        const key = cellKey(targetRowIndex, targetColIndex);
        const nextKeys = new Set(this.copiedKeys);
        nextKeys.add(key);

        // Only record the snapshot the FIRST time this cell is stamped in
        // the current session — a second stamp (e.g. dragging back over an
        // already-copied cell) must not overwrite the original pre-session
        // value with the now-copied one, or Cancel would "restore" to a
        // copied state instead of the true original.
        const nextSnapshots = this.restoreSnapshots.has(key)
            ? this.restoreSnapshots
            : new Map(this.restoreSnapshots).set(key, previousCellData);

        return new PlanogramClipboard(this.copySource, nextKeys, nextSnapshots, this.keyboardClipboard);
    }

    /**
     * Reverses markCopied for ONE target cell — removes it from
     * copiedKeys/restoreSnapshots (UI bookkeeping only; the caller is
     * responsible for actually restoring the cell's data via
     * onRestoreCell, using getRestoreSnapshot below to fetch the snapshot
     * BEFORE calling this, since this clears it).
     *
     * Added so clicking an already-copied target's checkbox a second time
     * can genuinely un-check it — previously, the only checkbox affordance
     * in copy mode was "copy here" (markCopied/copyToCell), with no way to
     * remove a single cell from the copied set short of cancelling the
     * entire copy-mode session via the toolbar's Cancel button. A no-op on
     * the source cell itself or a cell that was never copied, matching
     * markCopied's own guard shape.
     */
    unmarkCopied(targetRowIndex: number, targetColIndex: number): PlanogramClipboard {
        if (!this.copySource) return this;
        if (this.copySource.rowIndex === targetRowIndex && this.copySource.colIndex === targetColIndex) return this;

        const key = cellKey(targetRowIndex, targetColIndex);
        if (!this.copiedKeys.has(key)) return this;

        const nextKeys = new Set(this.copiedKeys);
        nextKeys.delete(key);

        const nextSnapshots = new Map(this.restoreSnapshots);
        nextSnapshots.delete(key);

        return new PlanogramClipboard(this.copySource, nextKeys, nextSnapshots, this.keyboardClipboard);
    }

    /** Reads back whatever was captured for this cell when it was first copied into this session — undefined if it was never copied (or this isn't copy mode). Read this BEFORE calling unmarkCopied, which clears the entry. */
    getRestoreSnapshot(rowIndex: number, colIndex: number): unknown {
        return this.restoreSnapshots.get(cellKey(rowIndex, colIndex));
    }

    /**
     * Same as markCopied but for a whole range at once (drag-select /
     * row-or-column "select all" copy). `snapshots` must be captured by
     * the CALLER before it runs onDuplicateCell on any of these cells —
     * unlike markCopied (called once per cell, so it can read-then-write
     * inline), a range is overwritten in a batch first and only reported
     * here afterward, so by the time this method runs every cell in
     * `keys` already holds its NEW (copied) value. There is no "lazily
     * fetch the old value" option for a range; the caller has no choice
     * but to snapshot everything up front. Only cells not already
     * snapshotted earlier this session take their entry from `snapshots`;
     * cells already captured keep their original entry untouched.
     */
    markCopiedMany(
        keys: Iterable<CellKey>,
        snapshots: ReadonlyMap<CellKey, unknown> = new Map(),
    ): PlanogramClipboard {
        if (!this.copySource) return this;
        const nextKeys = new Set(this.copiedKeys);
        const nextSnapshots = new Map(this.restoreSnapshots);

        for (const key of keys) {
            const { rowIndex, colIndex } = parseCellKey(key);
            if (rowIndex === this.copySource.rowIndex && colIndex === this.copySource.colIndex) continue;
            nextKeys.add(key);
            if (!nextSnapshots.has(key) && snapshots.has(key)) {
                nextSnapshots.set(key, snapshots.get(key));
            }
        }
        return new PlanogramClipboard(this.copySource, nextKeys, nextSnapshots, this.keyboardClipboard);
    }

    isCopied(rowIndex: number, colIndex: number): boolean {
        return this.copiedKeys.has(cellKey(rowIndex, colIndex));
    }

    /** True if every key in `keys` (excluding the source itself) has been copied — drives "select all" header checkmarks. */
    areAllCopied(keys: CellKey[]): boolean {
        if (keys.length === 0) return false;
        return keys.every(key => {
            const { rowIndex, colIndex } = parseCellKey(key);
            return this.isCopySourceCell(rowIndex, colIndex) || this.isCopied(rowIndex, colIndex);
        });
    }

    // -----------------------------------------------------------------------
    // Keyboard clipboard
    // -----------------------------------------------------------------------

    copyToKeyboardClipboard(cells: CellCoordinates[], isCut: boolean): PlanogramClipboard {
        if (cells.length === 0) return this;

        const minRow = Math.min(...cells.map(c => c.rowIndex));
        const minCol = Math.min(...cells.map(c => c.colIndex));

        return new PlanogramClipboard(this.copySource, this.copiedKeys, this.restoreSnapshots, {
            cells,
            anchor: { rowIndex: minRow, colIndex: minCol },
            isCut,
        });
    }

    /**
     * Given a paste anchor, computes the list of (source -> target) coordinate
     * pairs to replay via onDuplicateCell, skipping anything that would fall
     * outside [0, rows) x [0, columns). Pure math — no side effects.
     *
     * This is the "paste a copied BLOCK, repositioned at a single anchor
     * cell" case: every cell that was copied keeps its position relative to
     * the others, just shifted so the original anchor cell lands on
     * `pasteAnchor`. This is the right model when the user pastes into a
     * single active cell (no multi-cell selection) — see
     * computePasteIntoSelection below for the other paste model (GH #555),
     * which this method is NOT meant to handle.
     */
    computePaste(
        pasteAnchor: CellCoordinates,
        rows: number,
        columns: number,
    ): Array<{ source: CellCoordinates; target: CellCoordinates }> {
        if (!this.keyboardClipboard) return [];

        const { cells, anchor } = this.keyboardClipboard;
        const pairs: Array<{ source: CellCoordinates; target: CellCoordinates }> = [];

        cells.forEach(source => {
            const targetRow = pasteAnchor.rowIndex + (source.rowIndex - anchor.rowIndex);
            const targetCol = pasteAnchor.colIndex + (source.colIndex - anchor.colIndex);

            if (targetRow < 0 || targetCol < 0 || targetRow >= rows || targetCol >= columns) return;

            pairs.push({ source, target: { rowIndex: targetRow, colIndex: targetCol } });
        });

        return pairs;
    }

    /**
     * *** FIX (GH #555): Ctrl+V into a multi-cell selection must fill EVERY
     * selected cell with the copied content, not just one. ***
     *
     * Before this fix, the keydown handler always built a single-point
     * `pasteAnchor` (the active cell) and ran it through computePaste()
     * above — whose offset-block math only ever produces ONE target pair
     * when the clipboard holds a single copied cell (offset (0,0) relative
     * to itself). With several cells selected, that meant only the one
     * cell that happened to be the "active" cell in the selection got
     * pasted into; every other selected cell was left untouched. That's
     * exactly the reported "only the first cell gets filled" symptom.
     *
     * This is a genuinely different paste model than computePaste's, not
     * just a generalization of it: when the clipboard holds exactly ONE
     * copied cell and the user has a real multi-cell selection, the
     * expected behavior (per spec, and matching Excel's own "copy one
     * cell, select a range, paste" behavior) is "stamp that one source's
     * content into every selected cell" — there is no relative offset/
     * block-shape to preserve, because there was only ever one cell
     * copied. Reusing computePaste's relative-position math for this case
     * would be the wrong model even if it happened to be coaxed into
     * looping over targets — it answers "where would each cell of a
     * multi-cell COPY land", not "which cells does this PASTE fill".
     *
     * If more than one cell was copied (a block copy), this returns null
     * so the caller falls back to the single-anchor computePaste() above —
     * pasting a multi-cell block into a multi-cell selection isn't part of
     * the GH #555 spec and is intentionally left as the existing
     * single-anchor behavior.
     */
    computePasteIntoSelection(
        selectedKeys: CellKey[],
    ): Array<{ source: CellCoordinates; target: CellCoordinates }> | null {
        if (!this.keyboardClipboard) return null;
        if (selectedKeys.length <= 1) return null;

        const { cells } = this.keyboardClipboard;
        if (cells.length !== 1) return null;

        const source = cells[0];
        return selectedKeys.map(key => ({ source, target: parseCellKey(key) }));
    }

    /** Cut clipboard is consumed after its first paste — matches standard cut/paste semantics (Office, Figma, file managers). */
    consumeIfCut(): PlanogramClipboard {
        if (!this.keyboardClipboard?.isCut) return this;
        return new PlanogramClipboard(this.copySource, this.copiedKeys, this.restoreSnapshots, null);
    }
}